import { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  getRecordings,
  getProjects,
  getTranscript,
  downloadTranscript,
  Recording,
  Project,
} from '../services/api'

const glassPanel = {
  background: 'rgba(15, 23, 42, 0.6)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(100, 180, 255, 0.1)',
}

export default function GlobalRecordings() {
  const [statusFilter, setStatusFilter] = useState('all')
  const [projectFilter, setProjectFilter] = useState('all')
  const [sortBy, setSortBy] = useState('date')
  const [search, setSearch] = useState('')

  // Transcript view state
  const [showTranscriptModal, setShowTranscriptModal] = useState(false)
  const [viewingTranscript, setViewingTranscript] = useState<{ sections: Array<{ start_time: number; speaker?: string; text: string }> } | null>(null)
  const [transcriptRecording, setTranscriptRecording] = useState<Recording | null>(null)

  const { data: recordings, isLoading: loadingRecordings } = useQuery({
    queryKey: ['recordings'],
    queryFn: () => getRecordings().then((res) => res.data),
    refetchInterval: 10000,
  })

  const { data: projects } = useQuery({
    queryKey: ['projects'],
    queryFn: () => getProjects().then((res) => res.data),
  })

  // Create a project lookup map
  const projectMap = useMemo(() => {
    const map: Record<string, Project> = {}
    if (projects) {
      projects.forEach((p) => { map[p.id] = p })
    }
    return map
  }, [projects])

  const handleViewTranscript = async (recording: Recording) => {
    try {
      const response = await getTranscript(recording.id)
      setViewingTranscript(response.data)
      setTranscriptRecording(recording)
      setShowTranscriptModal(true)
    } catch (error) {
      console.error('Failed to load transcript:', error)
    }
  }

  const handleDownloadTranscript = async (recording: Recording) => {
    try {
      await downloadTranscript(recording.id, `${recording.original_filename || 'transcript'}.txt`)
    } catch (error) {
      console.error('Failed to download transcript:', error)
    }
  }

  // Compute stats
  const stats = useMemo(() => {
    if (!recordings) return { total: 0, transcribed: 0, processing: 0, totalDuration: 0 }
    return {
      total: recordings.length,
      transcribed: recordings.filter((r) => r.status === 'transcribed' || r.status === 'ready').length,
      processing: recordings.filter((r) => r.status === 'transcribing' || r.status === 'processing').length,
      totalDuration: recordings.reduce((sum, r) => sum + (r.duration_seconds || 0), 0),
    }
  }, [recordings])

  const filteredRecordings = useMemo(() => {
    if (!recordings) return []
    let filtered = [...recordings]

    // Apply search filter
    if (search) {
      const searchLower = search.toLowerCase()
      filtered = filtered.filter((r) => {
        const matchesFilename = r.original_filename.toLowerCase().includes(searchLower)
        const project = projectMap[r.project_id]
        const matchesProject = project?.project_name.toLowerCase().includes(searchLower) ||
          project?.customer_name.toLowerCase().includes(searchLower)
        return matchesFilename || matchesProject
      })
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter((r) => r.status === statusFilter)
    }

    // Apply project filter
    if (projectFilter !== 'all') {
      filtered = filtered.filter((r) => r.project_id === projectFilter)
    }

    // Sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.original_filename.localeCompare(b.original_filename)
        case 'size':
          return b.file_size - a.file_size
        case 'duration':
          return (b.duration_seconds || 0) - (a.duration_seconds || 0)
        case 'project':
          const projA = projectMap[a.project_id]?.project_name || ''
          const projB = projectMap[b.project_id]?.project_name || ''
          return projA.localeCompare(projB)
        default:
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      }
    })

    return filtered
  }, [recordings, statusFilter, projectFilter, sortBy, search, projectMap])

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  const formatDuration = (seconds?: number) => {
    if (!seconds) return '--:--'
    const hours = Math.floor(seconds / 3600)
    const mins = Math.floor((seconds % 3600) / 60)
    const secs = Math.floor(seconds % 60)
    if (hours > 0) {
      return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const formatTotalDuration = (seconds: number) => {
    const hours = (seconds / 3600).toFixed(1)
    return `${hours}h`
  }

  const getStatusBadge = (status: Recording['status']) => {
    switch (status) {
      case 'transcribed':
      case 'ready':
        return (
          <span className="px-3 py-1 text-xs font-medium rounded-full flex items-center space-x-1"
            style={{ background: 'rgba(52, 211, 153, 0.2)', color: '#34d399', border: '1px solid rgba(52, 211, 153, 0.3)' }}>
            <i className="fa-solid fa-check"></i>
            <span>Transcribed</span>
          </span>
        )
      case 'transcribing':
      case 'processing':
        return (
          <span className="px-3 py-1 text-xs font-medium rounded-full flex items-center space-x-1"
            style={{ background: 'rgba(251, 191, 36, 0.2)', color: '#fbbf24', border: '1px solid rgba(251, 191, 36, 0.3)' }}>
            <i className="fa-solid fa-spinner fa-spin"></i>
            <span>Transcribing</span>
          </span>
        )
      case 'error':
        return (
          <span className="px-3 py-1 text-xs font-medium rounded-full flex items-center space-x-1"
            style={{ background: 'rgba(248, 113, 113, 0.2)', color: '#f87171', border: '1px solid rgba(248, 113, 113, 0.3)' }}>
            <i className="fa-solid fa-times"></i>
            <span>Error</span>
          </span>
        )
      default:
        return (
          <span className="px-3 py-1 text-xs font-medium rounded-full flex items-center space-x-1"
            style={{ background: 'rgba(148, 163, 184, 0.2)', color: 'rgba(148, 163, 184, 0.8)', border: '1px solid rgba(148, 163, 184, 0.3)' }}>
            <i className="fa-solid fa-circle text-[8px]"></i>
            <span>Uploaded</span>
          </span>
        )
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: '#e2e8f0' }}>Transcripts & Audio</h1>
          <p className="text-sm mt-1" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>All recordings across projects</p>
        </div>
        <div className="relative">
          <input
            type="text"
            placeholder="Search recordings..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-80 pl-10 pr-4 py-2.5 rounded-xl text-sm"
            style={{ background: 'rgba(30, 41, 59, 0.5)', border: '1px solid rgba(100, 180, 255, 0.15)', color: '#e2e8f0' }}
          />
          <i className="fa-solid fa-search absolute left-3 top-3" style={{ color: 'rgba(148, 163, 184, 0.5)' }}></i>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="rounded-xl p-6 transition-all duration-300 hover:scale-[1.02]" style={glassPanel}>
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-lg flex items-center justify-center"
              style={{ background: 'rgba(167, 139, 250, 0.15)', border: '1px solid rgba(167, 139, 250, 0.3)' }}>
              <i className="fa-solid fa-microphone text-xl" style={{ color: '#a78bfa' }}></i>
            </div>
          </div>
          <h4 className="text-sm font-medium mb-1" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>Total Recordings</h4>
          <p className="text-3xl font-bold" style={{ color: '#e2e8f0' }}>{stats.total}</p>
        </div>

        <div className="rounded-xl p-6 transition-all duration-300 hover:scale-[1.02]" style={glassPanel}>
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-lg flex items-center justify-center"
              style={{ background: 'rgba(52, 211, 153, 0.15)', border: '1px solid rgba(52, 211, 153, 0.3)' }}>
              <i className="fa-solid fa-check-circle text-xl" style={{ color: '#34d399' }}></i>
            </div>
          </div>
          <h4 className="text-sm font-medium mb-1" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>Transcribed</h4>
          <p className="text-3xl font-bold" style={{ color: '#e2e8f0' }}>{stats.transcribed}</p>
        </div>

        <div className="rounded-xl p-6 transition-all duration-300 hover:scale-[1.02]" style={glassPanel}>
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-lg flex items-center justify-center"
              style={{ background: 'rgba(251, 191, 36, 0.15)', border: '1px solid rgba(251, 191, 36, 0.3)' }}>
              <i className="fa-solid fa-spinner text-xl" style={{ color: '#fbbf24' }}></i>
            </div>
          </div>
          <h4 className="text-sm font-medium mb-1" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>Processing</h4>
          <p className="text-3xl font-bold" style={{ color: '#e2e8f0' }}>{stats.processing}</p>
        </div>

        <div className="rounded-xl p-6 transition-all duration-300 hover:scale-[1.02]" style={glassPanel}>
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-lg flex items-center justify-center"
              style={{ background: 'rgba(96, 165, 250, 0.15)', border: '1px solid rgba(96, 165, 250, 0.3)' }}>
              <i className="fa-solid fa-clock text-xl" style={{ color: '#60a5fa' }}></i>
            </div>
          </div>
          <h4 className="text-sm font-medium mb-1" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>Total Duration</h4>
          <p className="text-3xl font-bold" style={{ color: '#e2e8f0' }}>{formatTotalDuration(stats.totalDuration)}</p>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="rounded-xl p-4" style={glassPanel}>
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center space-x-3">
            <span className="text-sm font-medium" style={{ color: 'rgba(148, 163, 184, 0.8)' }}>Filter:</span>
            <select
              value={projectFilter}
              onChange={(e) => setProjectFilter(e.target.value)}
              className="text-sm rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              style={{ background: 'rgba(30, 41, 59, 0.8)', border: '1px solid rgba(100, 180, 255, 0.2)', color: '#e2e8f0' }}
            >
              <option value="all">All Projects</option>
              {projects?.map((p) => (
                <option key={p.id} value={p.id}>{p.project_name}</option>
              ))}
            </select>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="text-sm rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              style={{ background: 'rgba(30, 41, 59, 0.8)', border: '1px solid rgba(100, 180, 255, 0.2)', color: '#e2e8f0' }}
            >
              <option value="all">All Status</option>
              <option value="uploaded">Uploaded</option>
              <option value="transcribing">Transcribing</option>
              <option value="transcribed">Transcribed</option>
              <option value="error">Error</option>
            </select>
          </div>
          <div className="flex items-center space-x-3">
            <span className="text-sm font-medium" style={{ color: 'rgba(148, 163, 184, 0.8)' }}>Sort:</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="text-sm rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              style={{ background: 'rgba(30, 41, 59, 0.8)', border: '1px solid rgba(100, 180, 255, 0.2)', color: '#e2e8f0' }}
            >
              <option value="date">Date</option>
              <option value="name">Name</option>
              <option value="size">Size</option>
              <option value="duration">Duration</option>
              <option value="project">Project</option>
            </select>
          </div>
        </div>
      </div>

      {/* Recordings List */}
      {loadingRecordings ? (
        <div className="flex items-center justify-center h-32">
          <div className="text-center">
            <i className="fa-solid fa-spinner fa-spin text-4xl mb-4" style={{ color: '#60a5fa' }}></i>
            <p style={{ color: 'rgba(148, 163, 184, 0.8)' }}>Loading recordings...</p>
          </div>
        </div>
      ) : filteredRecordings.length > 0 ? (
        <div className="space-y-4">
          {filteredRecordings.map((recording) => {
            const project = projectMap[recording.project_id]
            return (
              <div
                key={recording.id}
                className="rounded-xl p-6 transition-all duration-300 hover:scale-[1.01]"
                style={{ ...glassPanel, borderColor: recording.status === 'error' ? 'rgba(248, 113, 113, 0.3)' : 'rgba(100, 180, 255, 0.1)' }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4 flex-1">
                    <div
                      className="w-12 h-12 rounded-lg flex items-center justify-center"
                      style={{
                        background: recording.status === 'error' ? 'rgba(248, 113, 113, 0.15)' : 'rgba(167, 139, 250, 0.15)',
                        border: recording.status === 'error' ? '1px solid rgba(248, 113, 113, 0.3)' : '1px solid rgba(167, 139, 250, 0.3)'
                      }}
                    >
                      <i
                        className="fa-solid fa-microphone text-xl"
                        style={{ color: recording.status === 'error' ? '#f87171' : '#a78bfa' }}
                      ></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-base font-semibold mb-1" style={{ color: '#e2e8f0' }}>
                        {recording.original_filename}
                      </h4>
                      <div className="flex items-center space-x-4 text-sm flex-wrap gap-2" style={{ color: 'rgba(148, 163, 184, 0.6)' }}>
                        {project && (
                          <Link
                            to={`/projects/${project.id}`}
                            className="flex items-center space-x-1 hover:text-blue-400 transition"
                          >
                            <i className="fa-solid fa-folder text-xs"></i>
                            <span>{project.project_name}</span>
                          </Link>
                        )}
                        <span className="flex items-center space-x-1">
                          <i className="fa-solid fa-database text-xs"></i>
                          <span>{formatFileSize(recording.file_size)}</span>
                        </span>
                        <span className="flex items-center space-x-1">
                          <i className="fa-solid fa-clock text-xs"></i>
                          <span>{formatDuration(recording.duration_seconds)}</span>
                        </span>
                        <span className="flex items-center space-x-1">
                          <i className="fa-solid fa-calendar text-xs"></i>
                          <span>{new Date(recording.created_at).toLocaleDateString()}</span>
                        </span>
                      </div>
                      {recording.error_message && (
                        <p className="text-sm flex items-center space-x-1 mt-2" style={{ color: '#f87171' }}>
                          <i className="fa-solid fa-exclamation-triangle"></i>
                          <span>{recording.error_message}</span>
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    {getStatusBadge(recording.status)}
                    {(recording.status === 'transcribed' || recording.status === 'ready') && (
                      <>
                        <button
                          onClick={() => handleViewTranscript(recording)}
                          title="View transcript"
                          className="p-2 rounded-lg transition"
                          style={{ color: 'rgba(148, 163, 184, 0.6)' }}
                          onMouseOver={(e) => { e.currentTarget.style.color = '#60a5fa'; e.currentTarget.style.background = 'rgba(96, 165, 250, 0.1)' }}
                          onMouseOut={(e) => { e.currentTarget.style.color = 'rgba(148, 163, 184, 0.6)'; e.currentTarget.style.background = 'transparent' }}
                        >
                          <i className="fa-solid fa-eye"></i>
                        </button>
                        <button
                          onClick={() => handleDownloadTranscript(recording)}
                          title="Download transcript"
                          className="p-2 rounded-lg transition"
                          style={{ color: 'rgba(148, 163, 184, 0.6)' }}
                          onMouseOver={(e) => { e.currentTarget.style.color = '#60a5fa'; e.currentTarget.style.background = 'rgba(96, 165, 250, 0.1)' }}
                          onMouseOut={(e) => { e.currentTarget.style.color = 'rgba(148, 163, 184, 0.6)'; e.currentTarget.style.background = 'transparent' }}
                        >
                          <i className="fa-solid fa-download"></i>
                        </button>
                      </>
                    )}
                    <Link
                      to={`/projects/${recording.project_id}/recordings`}
                      className="px-4 py-2 rounded-lg text-sm font-medium transition"
                      style={{ background: 'rgba(96, 165, 250, 0.15)', border: '1px solid rgba(96, 165, 250, 0.3)', color: '#60a5fa' }}
                    >
                      View in Project
                    </Link>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      ) : (
        <div className="rounded-xl p-12 text-center" style={glassPanel}>
          <div className="w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4"
            style={{ background: 'rgba(100, 116, 139, 0.2)', border: '1px solid rgba(100, 180, 255, 0.1)' }}>
            <i className="fa-solid fa-microphone text-3xl" style={{ color: 'rgba(148, 163, 184, 0.5)' }}></i>
          </div>
          <h3 className="text-lg font-semibold mb-2" style={{ color: '#e2e8f0' }}>
            {search || statusFilter !== 'all' || projectFilter !== 'all' ? 'No recordings match your filters' : 'No recordings yet'}
          </h3>
          <p className="mb-6" style={{ color: 'rgba(148, 163, 184, 0.8)' }}>
            {search || statusFilter !== 'all' || projectFilter !== 'all'
              ? 'Try adjusting your search or filter criteria'
              : 'Upload recordings in your projects to get started'}
          </p>
          {!search && statusFilter === 'all' && projectFilter === 'all' && (
            <Link
              to="/projects"
              className="px-6 py-3 rounded-lg text-sm font-medium transition inline-flex items-center space-x-2"
              style={{ background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.3) 0%, rgba(139, 92, 246, 0.3) 100%)', border: '1px solid rgba(96, 165, 250, 0.4)', color: '#e2e8f0' }}
            >
              <i className="fa-solid fa-folder"></i>
              <span>Go to Projects</span>
            </Link>
          )}
        </div>
      )}

      {/* Transcript View Modal */}
      {showTranscriptModal && viewingTranscript && transcriptRecording && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50" style={{ backdropFilter: 'blur(4px)' }}>
          <div className="rounded-xl w-full max-w-4xl max-h-[90vh] overflow-hidden"
            style={{ background: 'rgba(15, 23, 42, 0.95)', border: '1px solid rgba(100, 180, 255, 0.2)' }}>
            <div className="p-6" style={{ borderBottom: '1px solid rgba(100, 180, 255, 0.1)' }}>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center"
                    style={{ background: 'rgba(167, 139, 250, 0.15)', border: '1px solid rgba(167, 139, 250, 0.3)' }}>
                    <i className="fa-solid fa-file-lines" style={{ color: '#a78bfa' }}></i>
                  </div>
                  <div>
                    <h2 className="text-xl font-bold" style={{ color: '#e2e8f0' }}>Transcript</h2>
                    <p className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>{transcriptRecording.original_filename}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleDownloadTranscript(transcriptRecording)}
                    className="px-4 py-2 rounded-lg text-sm font-medium transition flex items-center space-x-2"
                    style={{ background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.3) 0%, rgba(139, 92, 246, 0.3) 100%)', border: '1px solid rgba(96, 165, 250, 0.4)', color: '#e2e8f0' }}
                  >
                    <i className="fa-solid fa-download"></i>
                    <span>Download</span>
                  </button>
                  <button
                    onClick={() => {
                      setShowTranscriptModal(false)
                      setViewingTranscript(null)
                      setTranscriptRecording(null)
                    }}
                    className="p-2 rounded-lg transition"
                    style={{ color: 'rgba(148, 163, 184, 0.6)' }}
                    onMouseOver={(e) => { e.currentTarget.style.background = 'rgba(100, 116, 139, 0.3)' }}
                    onMouseOut={(e) => { e.currentTarget.style.background = 'transparent' }}
                  >
                    <i className="fa-solid fa-times"></i>
                  </button>
                </div>
              </div>
            </div>

            <div className="p-6 overflow-y-auto max-h-[70vh]">
              <div className="space-y-4">
                {viewingTranscript.sections.map((section, index) => (
                  <div key={index} className="flex space-x-4">
                    <div className="flex-shrink-0 w-16 text-sm font-mono" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>
                      {formatDuration(section.start_time)}
                    </div>
                    <div className="flex-1">
                      {section.speaker && (
                        <div className="text-sm font-medium mb-1" style={{ color: '#60a5fa' }}>
                          {section.speaker}
                        </div>
                      )}
                      <p style={{ color: 'rgba(148, 163, 184, 0.9)' }}>{section.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
